import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { APP_CONFIG, AuthService, EnumRouteName, LsTabbarComponent, MusicPlayerService, PlayerBarComponent, PlayerBarService, ToastService, UserPreferenceService, LanguageService } from '@lyricstream/shared';
import { filter, map, startWith } from 'rxjs/operators';
import { AppFooterComponent } from './shared/components/app-footer/app-footer.component';
import { AppHeaderComponent } from './shared/components/app-header/app-header.component';
import { LoginPopupOverlayComponent } from './shared/components/login-popup-overlay/login-popup-overlay';
import { ConsentBannerComponent } from './shared/components/consent-banner/consent-banner.component';

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, AppHeaderComponent, AppFooterComponent, PlayerBarComponent, LsTabbarComponent, LoginPopupOverlayComponent, ConsentBannerComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly appConfig = inject(APP_CONFIG);
  private readonly playerBarService = inject(PlayerBarService);
  /** Jamais utilisé directement ici — l'injection force juste la construction du service
   *  (racine, montée au tout premier chargement) pour que son constructeur restaure l'état
   *  audio persisté (piste/position) même si aucune piste n'a encore été jouée dans cette
   *  session. Sans ça : PlayerBarComponent (qui l'injecte normalement) ne se monte que si
   *  isPlayerActive() est déjà vrai — et ce flag n'est mis à vrai QUE par la restauration
   *  elle-même, donc rien ne se restaure jamais sur un rechargement de page (la barre
   *  disparaissait et le son coupait au refresh). */
  private readonly musicPlayerService = inject(MusicPlayerService);
  protected readonly auth = inject(AuthService);
  private readonly prefService = inject(UserPreferenceService);
  private readonly languageService = inject(LanguageService);
  /** Persisté en localStorage (pas juste en mémoire du composant) : sans ça, l'effet se
   *  redéclenchait à CHAQUE rechargement de page (le flag repart à false au reload), ramenant de
   *  force l'utilisateur à sa langue enregistrée et rendant impossible tout changement de langue
   *  via l'URL. On n'applique donc la préférence qu'une seule fois, jamais plus ensuite — l'URL
   *  fait foi pour tout le reste de la navigation. */
  private readonly LANG_APPLIED_KEY = 'ls_lang_pref_applied';
  /**
   * Monté ici (racine, toujours dans le DOM quelle que soit la route) plutôt que dans
   * admin-layout : ce dernier ne couvre que /admin, alors que des pages hors admin (ex.
   * features/search) ont aussi besoin d'un toast — cf. décision utilisateur "pk le toast n'est
   * pas réutilisable genre un composant" / "pk ce n'est pas un service". Un seul point de
   * montage pour toute l'app plutôt qu'un par layout.
   */
  protected readonly toastService = inject(ToastService);

  constructor() {
    this.applyLayoutConfig();

    effect(() => {
      if (this.auth.isAuthenticated()) {
        this.prefService.loadMyPreferences();
      }
    });

    // Applique la langue de contenu/interface sauvegardée en BD dès qu'on la connaît — une seule
    // fois, jamais plus (cf. commentaire sur LANG_APPLIED_KEY) — sinon un utilisateur qui s'est
    // connecté sur un autre appareil devrait rechanger sa langue manuellement à chaque connexion
    // (cf. "il faut aussi le charger").
    effect(() => {
      if (!this.isBrowser || !this.auth.isAuthenticated() || localStorage.getItem(this.LANG_APPLIED_KEY)) return;
      const prefs = this.prefService.preferences();
      if (!prefs?.language) return;
      localStorage.setItem(this.LANG_APPLIED_KEY, '1');
      if (this.languageService.currentLang().value !== prefs.language) {
        this.languageService.switchLang(prefs.language, false);
      }
    });
  }

  /**
   * Applique `environment.layout` une seule fois au démarrage — un seul point de lecture de la
   * config, plutôt que chaque composant (header/footer/pages) réimplémentant sa propre logique.
   * `--ls-shell-max` piloté ici est déjà consommé par le design system (cf. _tokens.scss) ; les
   * attributs `data-*-full-width` permettent à chaque zone (header/footer/body) de suivre son
   * propre réglage indépendamment — cf. décision utilisateur "pour chaque header, footer, body
   * tout avec des paramètres".
   */
  private applyLayoutConfig(): void {
    const layout = this.appConfig.layout;
    const root = this.document.documentElement;
    if (layout?.maxWidth) {
      root.style.setProperty('--ls-shell-max', layout.bodyFullWidth ? 'none' : layout.maxWidth);
    } else if (layout?.bodyFullWidth) {
      root.style.setProperty('--ls-shell-max', 'none');
    }
    // setAttribute plutôt que .dataset : le shim DOM utilisé côté SSR n'implémente pas
    // toujours .dataset sur documentElement (crash "Cannot set properties of undefined"),
    // alors que setAttribute est universellement supporté.
    root.setAttribute('data-header-full-width', String(!!layout?.headerFullWidth));
    root.setAttribute('data-footer-full-width', String(!!layout?.footerFullWidth));
    root.setAttribute('data-body-full-width', String(!!layout?.bodyFullWidth));
  }

  protected readonly isPlayerActive = this.playerBarService.isActive;

  /** Segments de route où le champ de recherche du header est masqué. */
  protected readonly hideSearchOn = [EnumRouteName.Search];

  private readonly fullscreenRoutes = ['/karaoke', '/projection', `/${EnumRouteName.GetStarted}`];
  // '/admin' retiré (test de layout) : les pages admin s'affichent dans le layout public (en-tête + pied de page).

  private isFullscreen(url: string): boolean {
    return this.fullscreenRoutes.some((r) => url.includes(r));
  }

  protected readonly isFullscreenRoute = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => this.isFullscreen((e as NavigationEnd).urlAfterRedirects)),
      startWith(this.isFullscreen(this.router.url)),
    ),
    { initialValue: this.isFullscreen(this.router.url) },
  );

  /**
   * Routes sans tab bar mobile — sous-ensemble de fullscreenRoutes : karaoké/projection/
   * get-started n'ont pas de sens avec une nav en bas, contrairement à /admin (qui masque
   * header/footer public mais garde la tab bar, cf. décision utilisateur "la tab bar a
   * afficher dans toute les page en mode mobile" — publiques ET admin). /admin monte donc sa
   * propre <ls-tabbar> dans admin-layout plutôt que celle-ci (montée hors du router-outlet ici).
   */
  private readonly noTabbarRoutes = ['/karaoke', '/projection', `/${EnumRouteName.GetStarted}`];

  private hidesTabbar(url: string): boolean {
    return this.noTabbarRoutes.some((r) => url.includes(r));
  }

  protected readonly showTabbar = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => !this.hidesTabbar((e as NavigationEnd).urlAfterRedirects)),
      startWith(!this.hidesTabbar(this.router.url)),
    ),
    { initialValue: !this.hidesTabbar(this.router.url) },
  );

  /** Pages d'administration affichées dans le layout public : leur contenu suit la même largeur
   *  maximale (`--ls-shell-max`, donc aussi le mode pleine largeur) que l'en-tête et le pied de page. */
  protected readonly isAdminRoute = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => (e as NavigationEnd).urlAfterRedirects.includes('/admin')),
      startWith(this.router.url.includes('/admin')),
    ),
    { initialValue: this.router.url.includes('/admin') },
  );
}
